// Overlay MilkDrop (Butterchurn) por tile, renderizado LOCALMENTE no viewer.
//
// No modo MilkDrop do broadcast, o video transmitido é um dummy preto e o
// visual sai daqui: cada viewer renderiza o preset escolhido pelo broadcaster
// reagindo ao audio da propria stream. Zero bitrate de video, e o sync
// visual<->audio é perfeito por construção (mesma fonte, mesmo relogio).
//
// O tap de audio é so leitura: o analyser alimenta o butterchurn e um gain 0
// puxa o grafo ate o destination (sem som no output). O caminho de audio do
// <video> nao e tocado, entao pitch e sync que estabilizamos nao sao afetados.

interface ButterchurnLibs {
  createVisualizer: (ctx: AudioContext, canvas: HTMLCanvasElement, opts: { width: number; height: number; pixelRatio?: number }) => {
    connectAudio: (node: AnalyserNode) => void;
    loadPreset: (preset: unknown, blendTime: number) => void;
    render: () => void;
    setRendererSize: (width: number, height: number) => void;
  };
  default: ButterchurnLibs;
}

declare global {
  interface Window {
    butterchurn?: ButterchurnLibs;
    butterchurnPresets?: { getPresets: () => Record<string, unknown> };
  }
}

export interface MilkdropOverlay {
  setPreset: (name: string) => void;
  destroy: () => void;
}

const FALLBACK_PRESET = 'martin - another kind of groove';
const MAX_RENDER_WIDTH = 1280;
const MAX_RENDER_HEIGHT = 720;

let libsPromise: Promise<boolean> | null = null;
let customPresetsPromise: Promise<Record<string, unknown>> | null = null;
let sharedCtx: AudioContext | null = null;
let interactionBound = false;

function loadScriptOnce(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = src;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('falha ao carregar ' + src));
    document.head.appendChild(s);
  });
}

async function loadLibs(): Promise<boolean> {
  if (!libsPromise) {
    libsPromise = (async () => {
      try {
        await loadScriptOnce('./vendor/butterchurn.min.js');
        await loadScriptOnce('./vendor/butterchurnPresets.min.js');
      } catch (e) {
        console.warn('[milkdrop] libs indisponiveis', e);
        return false;
      }
      return !!(window.butterchurn?.default?.createVisualizer && window.butterchurnPresets?.getPresets);
    })();
  }
  return libsPromise;
}

// Presets convertidos de .milk (formato do MilkDrop classico) pro formato do
// butterchurn, servidos como JSON em /vendor. Nomes vivem no mesmo espaco dos
// do butterchurn-presets: o broadcaster manda o nome e o viewer resolve aqui.
function loadCustomPresets(): Promise<Record<string, unknown>> {
  if (!customPresetsPromise) {
    customPresetsPromise = fetch('./vendor/presets-custom.json')
      .then((r) => (r.ok ? r.json() : {}))
      .catch(() => ({}));
  }
  return customPresetsPromise;
}

// AudioContext compartilhado por todos os overlays da pagina. Navegadores
// exigem gesto do usuario para destravar o relogio de audio: tenta o resume
// ja (caso a pagina ja tenha sido interagida) e amarra um listener unico de
// primeiro gesto.
function ensureAudioContext(): AudioContext {
  if (!sharedCtx) {
    sharedCtx = new AudioContext();
    if (!interactionBound) {
      interactionBound = true;
      const resume = () => {
        sharedCtx?.resume().catch(() => {});
        for (const evt of ['pointerdown', 'keydown', 'touchstart']) {
          document.removeEventListener(evt, resume);
        }
      };
      for (const evt of ['pointerdown', 'keydown', 'touchstart']) {
        document.addEventListener(evt, resume, { passive: true });
      }
    }
    sharedCtx.resume().catch(() => {});
  }
  return sharedCtx;
}

export async function createMilkdropOverlay(
  host: HTMLElement,
  videoEl: HTMLVideoElement,
  preset: string,
  getAudioLeadMs?: () => number
): Promise<MilkdropOverlay | null> {
  if (!(await loadLibs())) return null;

  const ctx = ensureAudioContext();
  const canvas = document.createElement('canvas');
  canvas.className = 'milkdrop-canvas';
  // O butterchurn NUNCA seta o tamanho fisico do canvas (viewport interno e
  // texturas sao dele; o buffer do canvas e do chamador). Buffer e viewport
  // PRECISAM ser iguais e estaveis: sincronizar com o tamanho do tile via
  // ResizeObserver falhou (tile muda de tamanho com o grid e o canvas ficava
  // travado num layout intermediario, cortando o render). Solucao: tamanho
  // FIXO 1280x720 e o CSS (inset 0, 100%) estica pra qualquer tile. Blurry
  // em tile gigente custa menos do que geometria quebrada.
  canvas.width = MAX_RENDER_WIDTH;
  canvas.height = MAX_RENDER_HEIGHT;
  // Posicao INLINE (e nao so via stylesheet): se o CSS do cliente estiver
  // cacheado numa versao anterior a .milkdrop-canvas, o canvas virava item
  // flex ao lado do <video> e era empurrado pra fora do tile (soh metade
  // visivel). Inline vence cache e garante a cobertura do tile.
  canvas.style.position = 'absolute';
  canvas.style.inset = '0';
  canvas.style.width = '100%';
  canvas.style.height = '100%';
  canvas.style.zIndex = '1';
  canvas.style.pointerEvents = 'none';
  host.appendChild(canvas);

  const visual = window.butterchurn!.default.createVisualizer(ctx, canvas, {
    width: MAX_RENDER_WIDTH,
    height: MAX_RENDER_HEIGHT,
    pixelRatio: 1,
  });
  console.log(`[milkdrop] overlay criado: canvas fixo ${canvas.width}x${canvas.height}, host ${Math.round(host.clientWidth)}x${Math.round(host.clientHeight)}`);

  const analyser = ctx.createAnalyser();
  // Tap do audio. O correto conceitualmente eh tapar o audio DO PROPRIO
  // ELEMENTO (videoEl.captureStream): o analyser passa a ouvir exatamente o
  // que o <video> esta tocando, no relogio dele, sem desync estrutural.
  // Tap live na stream de origem liam a fila na frente (adiantado do som que
  // sai do buffer do elemento), e compensar com estimativa nunca fecha de
  // verdade. Fallback pro tap live (sem captureStream) mantem o re-tap de
  // primeira carga (track sem dados -> source zumbi no Chrome) e o delay de
  // compensacao, que so faz sentido nesse modo.
  const delay = ctx.createDelay(2.0);
  delay.delayTime.value = 0;
  let tapLive = false;
  let tapStream: MediaStream | null = null;
  const el = videoEl as HTMLVideoElement & {
    captureStream?: () => MediaStream;
    mozCaptureStream?: () => MediaStream;
  };
  const capture = el.captureStream || el.mozCaptureStream;
  try {
    tapStream = capture ? capture.call(el) : null;
  } catch {
    tapStream = null;
  }
  if (!tapStream || tapStream.getAudioTracks().length === 0) {
    tapLive = true;
    tapStream = videoEl.srcObject as MediaStream | null;
    console.log('[milkdrop] captureStream indisponivel: tap live na stream (desync compensado por estimativa)');
  }
  let sourceNode: MediaStreamAudioSourceNode | null = null;
  function rebuildTap() {
    try {
      sourceNode?.disconnect();
    } catch {
      /* ignore */
    }
    sourceNode = null;
    if (tapStream && tapStream.getAudioTracks().length > 0) {
      try {
        sourceNode = ctx.createMediaStreamSource(tapStream);
        sourceNode.connect(delay);
      } catch (e) {
        console.warn('[milkdrop] tap de audio falhou, visual sem reacao ao som', e);
      }
    }
  }
  rebuildTap();
  delay.connect(analyser);
  // Analyser FOLHA (sem caminho pro destination) nao e processado no Chrome:
  // o grafo so roda puxado pelo destination. Gain 0 ate o destination puxa o
  // processamento SEM soar nada (o som do tile continua saindo pelo <video>).
  // Sem isso o butterchurn recebe so zeros: presets animam no tempo mas nao
  // reagem ao audio.
  const mute = ctx.createGain();
  mute.gain.value = 0;
  analyser.connect(mute);
  mute.connect(ctx.destination);
  visual.connectAudio(analyser);
  const freqScratch = new Uint8Array(64);
  const audioTracks = tapStream ? tapStream.getAudioTracks() : [];
  console.log(`[milkdrop] audio: tap=${tapLive ? 'live' : 'elemento'} tracks=${audioTracks.length} readyState=${audioTracks.map((t) => t.readyState).join(',')} ctx=${ctx.state}`);

  // Resolucao FIXA (ver comentario na criacao): sem ResizeObserver, sem
  // setRendererSize ao vivo. Self-check a cada ~2s loga canvas/viewport/host
  // pra diagnostico remoto.
  let running = true;
  let lastRenderAt = 0;
  let frameCount = 0;
  let tapSilentChecks = 0;
  let retapAttempts = 0;
  let lastDelayAdjustAt = 0;
  // Probe barato para testes: numero de frames renderizados e quantidade de
  // amostras com conteudo nao-preto (screenshot de canvas WebGL via CDP nem
  // sempre captura o frame composto, entao o assert usa readPixels aqui).
  const gl = canvas.getContext('webgl2') as WebGL2RenderingContext | null;
  const probe = { frames: 0, nonBlack: 0 };
  (window as unknown as Record<string, unknown>).__milkdrop = probe;
  (canvas as unknown as Record<string, unknown>).__milkdropProbe = probe;
  // Offset manual persistente (ms), somado ao lead medido. Pra calibrar no
  // olho quando o desync vem de latencia de SAIDA do dispositivo (fone/caixa/
  // Bluetooth), que a medicao interna nao ve: console ->
  // localStorage.setItem('stream-activity-milkdrop-sync-offset-ms', '150')
  let manualOffsetMs = 0;
  try {
    manualOffsetMs = Number(localStorage.getItem('stream-activity-milkdrop-sync-offset-ms')) || 0;
  } catch {
    /* ignore */
  }
  const renderLoop = () => {
    if (!running) return;
    const now = performance.now();
    // Compensacao: so faz sentido no modo tap LIVE (frente da fila vs buffer
    // do elemento). Com o tap do ELEMENTO o analyser ja ouve o que esta
    // tocando; so o offset manual (calibragem fina externa) continua valendo.
    if (getAudioLeadMs && tapLive && now - lastDelayAdjustAt > 500) {
      lastDelayAdjustAt = now;
      const lead = Math.min(1000, Math.max(0, getAudioLeadMs())) + manualOffsetMs;
      const target = lead / 1000;
      const cur = delay.delayTime.value;
      delay.delayTime.value = cur + (target - cur) * 0.3;
    } else if (!tapLive && now - lastDelayAdjustAt > 500) {
      lastDelayAdjustAt = now;
      const target = manualOffsetMs / 1000;
      const cur = delay.delayTime.value;
      if (Math.abs(cur - target) > 0.001) delay.delayTime.value = cur + (target - cur) * 0.3;
    }
    if (now - lastRenderAt >= 1000 / 60 - 0.5) {
      lastRenderAt = now;
      visual.render();
      frameCount++;
      if (frameCount % 120 === 0) {
        const vp = gl ? Array.from(gl.getParameter(gl.VIEWPORT)) : null;
        analyser.getByteFrequencyData(freqScratch);
        const freqMax = Math.max(...freqScratch);
        console.log(`[milkdrop] t${(frameCount / 60).toFixed(0)}s canvas ${canvas.width}x${canvas.height} viewport ${vp} host ${Math.round(host.clientWidth)}x${Math.round(host.clientHeight)} ctx=${ctx.state} freqMax=${freqMax} audioReady=${audioTracks.map((t) => t.readyState).join(',')}`);
        // Auto-cura do tap zumbi (ver comentario no rebuildTap): com o ctx
        // rodando e analisador em silencio por ~6s, recria o source node.
        // Desiste apos 3 tentativas: stream realmente sem audio.
        if (ctx.state === 'running') {
          if (freqMax === 0) tapSilentChecks++;
          else tapSilentChecks = 0;
          // Sem tapStream o rebuildTap eh no-op: nao loga re-tap falso.
          if (tapSilentChecks >= 3 && retapAttempts < 3 && tapStream) {
            tapSilentChecks = 0;
            retapAttempts++;
            console.log(`[milkdrop] audio silencioso com ctx rodando, re-tap #${retapAttempts}`);
            rebuildTap();
          }
        }
      }
      if (gl && frameCount % 30 === 0) {
        // Estado GL pode ficar sujo entre renders (FBO interno, scissor):
        // normaliza pro readPixels nao ler a textura intermediaria ou fora
        // da scissor area.
        gl.bindFramebuffer(gl.FRAMEBUFFER, null);
        gl.disable(gl.SCISSOR_TEST);
        const px = new Uint8Array(4);
        for (const [fx, fy] of [[0.5, 0.5], [0.25, 0.25], [0.75, 0.75], [0.5, 0.15], [0.15, 0.5]]) {
          gl.readPixels((canvas.width * fx) | 0, (canvas.height * fy) | 0, 1, 1, gl.RGBA, gl.UNSIGNED_BYTE, px);
          if (px[0] + px[1] + px[2] > 24) {
            probe.nonBlack++;
            break;
          }
        }
      }
      probe.frames = frameCount;
    }
    requestAnimationFrame(renderLoop);
  };
  requestAnimationFrame(renderLoop);

  const customPresets = await loadCustomPresets();
  function applyPreset(name: string) {
    const presets = window.butterchurnPresets!.getPresets();
    const chosen = customPresets[name] ?? presets[name] ?? presets[FALLBACK_PRESET];
    if (!chosen) {
      console.warn(`[milkdrop] preset nao encontrado: ${name}`);
      return;
    }
    if (!customPresets[name] && !presets[name]) console.warn(`[milkdrop] preset "${name}" inexistente, usando fallback`);
    visual.loadPreset(chosen, 0.7);
    console.log(`[milkdrop] preset: ${customPresets[name] || presets[name] ? name : FALLBACK_PRESET}`);
  }
  applyPreset(preset);

  return {
    setPreset: applyPreset,
    destroy: () => {
      running = false;
      // Libera o grafo inteiro do overlay (nao soh o analyser): source/delay/
      // gain ficariam conectados ao AudioContext compartilhado, acumulando
      // nos mortos a cada ciclo de watch/unwatch ou tile recriado.
      for (const node of [sourceNode, delay, analyser, mute]) {
        try {
          node?.disconnect();
        } catch {
          /* ignore */
        }
      }
      canvas.remove();
    },
  };
}
